import { gql, type ApolloClient } from "@apollo/client"
import { PathItemFragment } from "@pathwright/graphql-client/src/generated"
import { getApolloClient } from "../pathwright/PathwrightClient"

export const updateCachedPathItemBlocks = (
  item: PathItemFragment,
  blocks: PathItemFragment["blocks"],
  content_id = 0,
  client: ApolloClient<object> = getApolloClient({
    schoolId: window.school?.id
  })
) => {
  const itemKey = client.cache.identify({
    __typename: "PathItem",
    id: item.id
  })!
  // Omitted IDs in partial inputs must not erase a known association.
  // A supplied ID (including an explicit null) keeps its existing precedence.
  const itemContentId =
    !content_id && item.content_id === undefined
      ? client.cache.readFragment<Pick<PathItemFragment, "content_id">>({
          id: itemKey,
          fragment: gql`
            fragment CachedPathItemContentId on PathItem {
              content_id
            }
          `
        })?.content_id
      : item.content_id
  const sharedContentId = content_id || itemContentId
  const nextBlocks = blocks ? { __typename: "BlocksContent", ...blocks } : null

  client.cache.batch({
    update(cache) {
      // A partial cached item is enough; reading the entire PathItem fragment
      // can return null when unrelated fields have not been queried yet.
      cache.writeFragment({
        id: itemKey,
        fragment: gql`
          fragment CachedPathItemBlocks on PathItem {
            id
            content_id
            blocks {
              content_last_modified
              last_published
            }
          }
        `,
        data: {
          __typename: "PathItem",
          id: item.id,
          content_id: sharedContentId || null,
          blocks: nextBlocks
        }
      })

      // Source and Cohort Steps embed metadata for the same Blocks document.
      // Keep every cached copy current when that shared document is saved.
      if (sharedContentId) {
        for (const [id, cachedItem] of Object.entries(cache.extract())) {
          if (
            id !== itemKey &&
            cachedItem?.__typename === "PathItem" &&
            cachedItem.content_id === sharedContentId
          ) {
            // Use writeFragment instead of cache.modify since it also
            // adds metadata when this cached Step was queried without blocks,
            // while cache.modify only updates existing fields.
            cache.writeFragment({
              id,
              fragment: gql`
                fragment SharedPathItemBlocks on PathItem {
                  blocks {
                    content_last_modified
                    last_published
                  }
                }
              `,
              data: { __typename: "PathItem", blocks: nextBlocks }
            })
          }
        }
      }
    }
  })
}

export const getDocumentPublishMeta = (
  lastPublishedDateTime?: string,
  lastModifiedDateTime?: string
) => {
  const lastPublished = lastPublishedDateTime
    ? new Date(lastPublishedDateTime).toISOString()
    : null
  const contentLastModified = lastModifiedDateTime
    ? new Date(lastModifiedDateTime).toISOString()
    : lastPublished
    ? lastPublished
    : null

  const isPublished =
    lastPublished && lastPublished > (contentLastModified || 0)

  return {
    lastPublished,
    contentLastModified,
    isPublished
  }
}

// If resource and cohort ids are provided or in context,
// we return the full version of the Blocks Step context key (/resource/cohort/step)
// otherwise we simply provide the legacy step id version
export const getStepContextKey = (
  step_id: number,
  resource_id: number | null = null,
  cohort_id: number | null = null
) => {
  if (resource_id && cohort_id) return `/${resource_id}/${cohort_id}/${step_id}`

  const storeResource = window?.App?.getStore("resource")?.getState()?.resource
  const storeCohort = window?.App?.getStore("resource")?.getState()?.offering

  const resourceId = resource_id || storeResource?.id
  const cohortId = cohort_id || storeCohort?.id

  if (!resourceId || !cohortId) return `/${step_id}`

  return `/${resourceId}/${cohortId}/${step_id}`
}

export const getReportingContextKey = (resourceId: number) =>
  `reporting-${resourceId}`
